import { useEffect, useImperativeHandle, useRef } from "react";

/**
 * Widget de Cloudflare Turnstile.
 *
 * Carga el script bajo demanda —no en el index.html— para no gastar una
 * peticion a un tercero en el arranque de la app: el usuario solo lo necesita
 * al llegar al ultimo paso del formulario, y la prioridad del CLAUDE.md es el
 * tiempo de carga inicial.
 *
 * El token que produce es de un solo uso y expira a los ~5 minutos. Por eso
 * expone `onExpirado`: si el usuario tarda en revisar el resumen, hay que
 * refrescarlo antes de publicar.
 */

const URL_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let promesaScript = null;

/// Una sola carga del script aunque el componente se monte varias veces.
function cargarScript() {
  if (window.turnstile) return Promise.resolve();
  if (promesaScript) return promesaScript;

  promesaScript = new Promise((resolver, rechazar) => {
    const etiqueta = document.createElement("script");
    etiqueta.src = URL_SCRIPT;
    etiqueta.async = true;
    etiqueta.onload = () => resolver();
    etiqueta.onerror = () => {
      promesaScript = null;
      rechazar(new Error("No se pudo cargar la verificacion de seguridad."));
    };
    document.head.appendChild(etiqueta);
  });
  return promesaScript;
}

function Turnstile({ ref, onToken, onError, onExpirado }) {
  const contenedorRef = useRef(null);
  const widgetRef = useRef(null);
  // Guardar los callbacks en refs evita re-renderizar el widget cada vez que el
  // padre se actualiza: Turnstile no soporta que le cambien el contenedor.
  const callbacks = useRef({ onToken, onError, onExpirado });
  callbacks.current = { onToken, onError, onExpirado };

  const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!sitekey) {
      // Sin sitekey no se renderiza nada y el formulario sigue funcionando. El
      // backend decide si eso es aceptable: en produccion no arranca sin clave.
      console.warn("VITE_TURNSTILE_SITE_KEY sin definir: verificacion desactivada.");
      callbacks.current.onToken?.(null);
      return undefined;
    }

    let cancelado = false;

    cargarScript()
      .then(() => {
        if (cancelado || !contenedorRef.current) return;
        widgetRef.current = window.turnstile.render(contenedorRef.current, {
          sitekey,
          language: "es",
          callback: (token) => callbacks.current.onToken?.(token),
          "error-callback": () =>
            callbacks.current.onError?.(
              "No pudimos completar la verificacion de seguridad.",
            ),
          "expired-callback": () => callbacks.current.onExpirado?.(),
          "timeout-callback": () => callbacks.current.onExpirado?.(),
        });
      })
      .catch((error) => {
        if (!cancelado) callbacks.current.onError?.(error.message);
      });

    return () => {
      cancelado = true;
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  }, [sitekey]);

  // El padre necesita pedir un token nuevo tras un intento fallido: el token es
  // de un solo uso, asi que reintentar con el mismo siempre vuelve a fallar.
  useImperativeHandle(ref, () => ({
    reiniciar() {
      if (widgetRef.current && window.turnstile) {
        window.turnstile.reset(widgetRef.current);
      }
    },
  }), []);

  if (!sitekey) return null;
  return <div className="turnstile" ref={contenedorRef} />;
}

export default Turnstile;
